import { useEffect, useState, useCallback } from "react";
import { useAdminAuth } from "../../context/AdminAuthContext";
import { Plus, Pencil, Trash2, MapPin, Save, X } from "lucide-react";

const API_URL = process.env.REACT_APP_BACKEND_URL;

const formatRub = (n) =>
  new Intl.NumberFormat("ru-RU", { maximumFractionDigits: 0 }).format(n) + " ₽";

const emptyForm = { name: "", cityPatterns: "", cost: 0 };

export default function AdminDelivery() {
  const { admin } = useAdminAuth();
  const [zones, setZones] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // null | "new" | zone.id
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const fetchZones = useCallback(async () => {
    setLoading(true);
    try {
      const r = await fetch(`${API_URL}/api/admin/delivery/zones`, {
        headers: { Authorization: `Bearer ${admin.token}` },
      });
      if (r.ok) setZones(await r.json());
    } catch {/* ignore */}
    setLoading(false);
  }, [admin.token]);

  useEffect(() => { fetchZones(); }, [fetchZones]);

  const startEdit = (zone) => {
    setForm({
      name: zone.name,
      cityPatterns: zone.cityPatterns.join(", "),
      cost: zone.cost,
    });
    setEditing(zone.id);
  };

  const startNew = () => {
    setForm(emptyForm);
    setEditing("new");
  };

  const cancel = () => {
    setEditing(null);
    setForm(emptyForm);
  };

  const save = async () => {
    setSaving(true);
    try {
      const body = {
        name: form.name.trim(),
        cityPatterns: form.cityPatterns.split(",").map((s) => s.trim()).filter(Boolean),
        cost: Number(form.cost) || 0,
      };
      const url = editing === "new"
        ? `${API_URL}/api/admin/delivery/zones`
        : `${API_URL}/api/admin/delivery/zones/${editing}`;
      const r = await fetch(url, {
        method: editing === "new" ? "POST" : "PUT",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${admin.token}` },
        body: JSON.stringify(body),
      });
      if (!r.ok) throw new Error((await r.json()).detail || "Save failed");
      await fetchZones();
      cancel();
    } catch (e) {
      alert(e.message);
    }
    setSaving(false);
  };

  const remove = async (zoneId) => {
    if (!window.confirm("Удалить эту зону?")) return;
    await fetch(`${API_URL}/api/admin/delivery/zones/${zoneId}`, {
      method: "DELETE",
      headers: { Authorization: `Bearer ${admin.token}` },
    });
    fetchZones();
  };

  return (
    <div data-testid="admin-delivery-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-white text-2xl font-sans font-semibold">Зоны доставки</h1>
          <p className="text-[#666] text-sm font-sans mt-1">
            Курьерская доставка до двери. Используйте <code className="text-[#D8C08F]">*</code> как catchall.
          </p>
        </div>
        <button
          onClick={startNew}
          className="flex items-center gap-2 bg-[#D8C08F] text-black font-semibold px-4 py-2.5 rounded-lg text-sm font-sans"
          data-testid="zone-new"
        >
          <Plus size={16} /> Новая зона
        </button>
      </div>

      {editing && (
        <div className="bg-[#1a1a1a] border border-[#D8C08F]/30 rounded-xl p-4 mb-4" data-testid="zone-edit-card">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase">Название</label>
              <input
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
                placeholder="Москва и МО"
                className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg px-3 py-2 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F]"
                data-testid="zone-name"
              />
            </div>
            <div>
              <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase">Шаблоны городов (через запятую)</label>
              <input
                value={form.cityPatterns}
                onChange={(e) => setForm({ ...form, cityPatterns: e.target.value })}
                placeholder="москва, московск"
                className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg px-3 py-2 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F]"
                data-testid="zone-patterns"
              />
            </div>
            <div>
              <label className="block text-[#888] text-xs font-sans mb-1.5 uppercase">Цена (₽)</label>
              <input
                type="number"
                value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })}
                className="w-full bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg px-3 py-2 text-white text-sm font-sans focus:outline-none focus:border-[#D8C08F]"
                data-testid="zone-cost"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-3">
            <button
              onClick={save}
              disabled={saving || !form.name.trim() || !form.cityPatterns.trim()}
              className="flex items-center gap-2 bg-[#D8C08F] text-black font-semibold px-4 py-2 rounded-lg text-sm font-sans disabled:opacity-50"
              data-testid="zone-save"
            >
              <Save size={14} /> {editing === "new" ? "Создать" : "Сохранить"}
            </button>
            <button
              onClick={cancel}
              className="flex items-center gap-2 text-[#888] hover:text-white px-4 py-2 rounded-lg text-sm font-sans"
              data-testid="zone-cancel"
            >
              <X size={14} /> Отмена
            </button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="flex items-center justify-center h-40">
          <div className="w-6 h-6 border-2 border-[#333] border-t-[#D8C08F] rounded-full animate-spin" />
        </div>
      ) : zones.length === 0 ? (
        <div className="text-center py-16">
          <MapPin size={40} className="text-[#333] mx-auto mb-4" />
          <p className="text-[#666] text-sm font-sans">Зон пока нет. Создайте первую.</p>
        </div>
      ) : (
        <div className="space-y-2" data-testid="zones-list">
          {zones.map((z) => (
            <div
              key={z.id}
              className="bg-[#1a1a1a] border border-[#2a2a2a] rounded-xl p-4 flex items-center gap-3"
              data-testid={`zone-row-${z.id}`}
            >
              <MapPin size={18} className="text-[#666]" strokeWidth={1.6} />
              <div className="flex-1 min-w-0">
                <p className="text-white text-sm font-sans font-medium">{z.name}</p>
                <p className="text-[#666] text-xs font-sans truncate">
                  {z.cityPatterns.map((p) => p === "*" ? "(catchall)" : p).join(", ")}
                </p>
              </div>
              <div className="text-white text-sm font-sans font-semibold">{formatRub(z.cost)}</div>
              <button
                onClick={() => startEdit(z)}
                className="p-2 rounded-lg hover:bg-white/5 text-[#555] hover:text-white"
                data-testid={`zone-edit-${z.id}`}
              >
                <Pencil size={16} />
              </button>
              <button
                onClick={() => remove(z.id)}
                className="p-2 rounded-lg hover:bg-red-500/10 text-[#555] hover:text-red-400"
                data-testid={`zone-del-${z.id}`}
              >
                <Trash2 size={16} />
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
